// src/components/ServerDatabase/ServerCard.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

export const renderServerIcon = (iconType, iconUrl) => {
  if (iconUrl) {
    return <img src={iconUrl} alt="" className="w-full h-full object-cover" />;
  }

  switch (iconType) {
    case 'castle':
      return (
        <svg viewBox="0 0 24 24">
          <path d="M4 4h4v6h8V4h4v16h-4v-6H8v6H4z" />
        </svg>
      );
    case 'flame':
      return (
        <svg viewBox="0 0 24 24">
          <path d="m13 2-2 2.5h3L11 9l4.5-1.5L13 12h3l-5 10 2-7.5H10L13 2z" />
        </svg>
      );
    case 'shield':
      return (
        <svg viewBox="0 0 24 24">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case 'sword':
      return (
        <svg viewBox="0 0 24 24">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      );
    case 'spark':
      return (
        <svg viewBox="0 0 24 24">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case 'crown':
      return (
        <svg viewBox="0 0 24 24">
          <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14v2H5v-2z" />
        </svg>
      );
    case 'globe':
      return (
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" x2="22" y1="12" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24">
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M3 9h18M9 21V9" />
        </svg>
      );
  }
};

const ServerCard = ({
  server,
  onCopyIp,
  onOpenModal,
  onConnectRcon
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const fullAddress = server.port ? `${server.ip}:${server.port}` : server.ip;
  const percent = Math.min(
    100,
    Math.round((server.players / (server.maxPlayers || 1)) * 100)
  );

  const dataRoute = isEn ? '/en/server-data-extraction' : '/server-data-extraction';

  return (
    <div className="sdb-server-card">
      <div className="sdb-server-card-body">
        {/* Шапка карточки: Иконка + IP адрес + Кнопка копирования + Статус/Пинг */}
        <div className="sdb-server-card-head">
          <div className="sdb-server-identity-group">
            <div className="sdb-server-icon-badge">
              {renderServerIcon(server.iconType, server.iconUrl)}
            </div>
            <div className="sdb-server-identity-text flex flex-col justify-center min-w-0">
              <div className="sdb-server-address-row">
                <h3 className="sdb-server-address-title" title={fullAddress}>
                  {fullAddress}
                </h3>
                <button
                  type="button"
                  className="sdb-server-copy-icon-btn"
                  onClick={() => onCopyIp(fullAddress)}
                  title={isEn ? 'Copy IP:PORT' : 'Скопировать IP:PORT'}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
              <div className="sdb-server-tested-sub font-mono text-[0.70rem] text-white/40 tracking-tight select-none mt-0.5">
                {isEn ? `Tested: ${server.testedText}` : `Проверено: ${server.testedText}`}
              </div>
            </div>
          </div>

          {/* Пинг в стиле Modrinth с аккуратным индикатором */}
          <div className="sdb-server-ping-badge">
            <span className="sdb-ping-dot" />
            <span className="sdb-ping-val">{server.ping} ms</span>
          </div>
        </div>

        {/* Minecraft MOTD: чистый текст описания как в статьях/проектах Modrinth, без темной коробки с рамкой */}
        <div className="sdb-server-motd-clean">
          <div
            className="sdb-motd-line"
            dangerouslySetInnerHTML={{ __html: server.motdLine1 }}
          />
          <div
            className="sdb-motd-line"
            dangerouslySetInnerHTML={{ __html: server.motdLine2 }}
          />
        </div>

        {/* Единая строка метаданных: версия, ядро и онлайн игроков */}
        <div className="sdb-server-meta-bar">
          <div className="sdb-server-meta-tags-row">
            <span className="sdb-meta-badge">{server.version}</span>
            <span className="sdb-meta-badge">{server.core}</span>
          </div>

          <div className="sdb-server-players-stat">
            <svg viewBox="0 0 24 24" fill="none" className="sdb-players-icon">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.8" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="1.8" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.8" />
            </svg>
            <span className="sdb-players-num-val">
              {server.players.toLocaleString()}
              <span className="sdb-players-max-val"> / {server.maxPlayers.toLocaleString()}</span>
            </span>
          </div>
        </div>

        {/* Тонкий, аккуратный прогресс-бар онлайна без отдельного рамочного блока */}
        <div className="sdb-players-progress-track">
          <div
            className="sdb-players-progress-fill"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* Нижняя полоса действий */}
      <div className="sdb-server-card-footer">
        <div className="sdb-server-card-actions">
          <button
            type="button"
            className="sdb-action-btn-primary"
            onClick={() => onConnectRcon(fullAddress)}
            title={isEn ? 'Connect to RCON console' : 'Подключиться к RCON консоли'}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <polyline points="4 17 10 11 4 5" />
              <line x1="12" y1="19" x2="20" y2="19" />
            </svg>
            <span>{isEn ? 'RCON Console' : 'RCON Консоль'}</span>
          </button>

          <div className="sdb-card-secondary-actions">
            <Link
              to={`${dataRoute}?ip=${encodeURIComponent(fullAddress)}`}
              className="sdb-action-btn-secondary"
              title={isEn ? 'Extract server data' : 'Извлечь данные сервера'}
            >
              <svg viewBox="0 0 24 24" fill="none">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
              </svg>
              <span>{isEn ? 'Data' : 'Данные'}</span>
            </Link>

            <button
              type="button"
              className="sdb-action-btn-secondary"
              onClick={() => onOpenModal(server)}
              title={isEn ? 'Detailed info & exploits' : 'Подробные сведения и уязвимости'}
            >
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>{isEn ? 'Info' : 'Инфо'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerCard;